// Render hub: reconciles the DOM from state. Iframes are created once per tab
// and kept in a Map so navigation/clock ticks never reload them.

import { state, active, byId } from "./state.js";
import { tabsEl, contentEl, addrInput, spinner, backBtn, fwdBtn, folderEl, startEl, errorEl, loaderEl, loaderLabel } from "./dom.js";
import { selectTab, closeTab, moveTab, onDragStart, onDrop } from "./tabs.js";
import { renderFolderView } from "./folders.js";
import { decode, encode, PREFIXES, transportFor, remote } from "./proxy.js";
import { dispAddr, dispTitle, maxObf } from "./obf.js";
import { clearWatch, checkFrame, armProxyErrorCheck, renderError, renderWait, failInternal, pageLoaded } from "./errors.js";
import { record as recordHistory } from "./history.js";
import { applyZoom } from "./zoom.js";
import { updateStar } from "./favorites-ui.js";
import { openTabMenu, attachLongPress } from "./tab-menu.js";
import { applyExtFrame } from "./ext-frames.js";
import { saveSession } from "./session-store.js";
import { get } from "../shared/settings-store.js";
import { isPerf } from "./perf.js";
import { watchFrameKeys } from "./shortcuts.js";
import { hostKey as iconHost } from "../shared/site-icons.js";
import { icons, iconsAllowed, loadIcons } from "./icon-store.js";
import { isGamePath, playPass } from "./play-pass.js";

const PIN_SVG = '<svg width="11" height="11" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M9.5 1.5 6 5 3 6l3 3-3.5 3.5 1 1L10 10l3 3 1-1-1-3 3.5-3.5z"/></svg>';

const loadHost = (u) => { try { return new URL(u).hostname.replace(/^www\./, ""); } catch (e) { return ""; } };

export const frames = new Map(); // tab id -> iframe
// The island (Island Mode) draws its own tabs and redraws after every render.
let afterRender = null;
export const onRender = (fn) => { afterRender = fn; };
export const favOk = (src) => { const st = favStatus.get(src); if (st === undefined) probeFav(src); return st === "ok"; };

// Tab favicons (proxied tabs only). We never render an <img> per tab - renderTabs
// rebuilds the strip on every render(), so a fresh <img> would flicker/refetch.
// Instead we probe each proxied favicon URL once, cache its status, and only paint
// it (as a cached background-image) once it's confirmed to load - so a tab never
// shows a broken or blank icon box.
const favStatus = new Map(); // proxied favicon src -> "pending" | "ok" | "bad"
function probeFav(src, host) {
	if (favStatus.has(src)) return; // idempotent - never re-probe (and no render loop)
	favStatus.set(src, "pending"); // set BEFORE assigning .src
	const img = new Image();
	img.decoding = "async";
	img.referrerPolicy = "no-referrer";
	img.onload = () => {
		favStatus.set(src, "ok");
		// Keep a small copy for favorites/history tiles, so they never have to fetch one.
		if (host && iconsAllowed()) loadIcons().then((m) => { const d = m ? m.shrinkIcon(img) : ""; if (d) icons.save(host, d); });
		render(); // re-render to reveal it
	};
	img.onerror = () => { favStatus.set(src, "bad"); }; // nothing to show - no render
	img.src = src;
}

// Most-recently-active tab ids. The first `maxLiveTabs` keep a live iframe; the
// rest are suspended (iframe dropped) so many open tabs don't lag the BiB.
let lru = [];
function liveSetIds() {
	const a = active();
	if (a) { lru = lru.filter((id) => id !== a.id); lru.unshift(a.id); }
	const existing = new Set(state.tabs.map((t) => t.id));
	lru = lru.filter((id) => existing.has(id));
	state.tabs.forEach((t) => { if (lru.indexOf(t.id) === -1) lru.push(t.id); });
	let cap = Math.max(2, Math.min(30, Number(get("maxLiveTabs")) || 6));
	// Lite mode: hold far fewer live iframes so a stack of open tabs can't exhaust
	// memory on a 2-4GB Chromebook (suspended tabs reload instantly on reopen).
	if (isPerf()) cap = Math.min(cap, 3);
	const live = new Set(lru.slice(0, cap));
	// A Linux tab is never suspended: dropping its iframe would destroy a running virtual machine
	// (and everything since its last save). There is at most one, and it pauses itself when hidden.
	state.tabs.forEach((t) => { if (t.internal === "/linux") live.add(t.id); });
	// Nor is a page holding something unsaved (Settings with a carry code's last open). The copy is
	// kept on the device anyway (shared/carry-held.js); this just keeps the page as the student left it.
	state.tabs.forEach((t) => { if (t.internal && unsavedIn(t)) live.add(t.id); });
	return live;
}
// The same question app/linux-open.js unsavedIn() asks, kept here so this file needn't import it.
function unsavedIn(tab) {
	if (!tab || !tab.internal) return null;
	try {
		const w = frames.get(tab.id) && frames.get(tab.id).contentWindow;
		return (w && typeof w.revaultUnsaved === "function" && w.revaultUnsaved()) || null;
	} catch { return null; }
}

const CLOSE_SVG =
	'<svg width="11" height="11" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"><line x1="4" y1="4" x2="10" y2="10"></line><line x1="10" y1="4" x2="4" y2="10"></line></svg>';

function ensureFrame(tab) {
	let f = frames.get(tab.id);
	// Sandbox mode: a proxied tab's page lives in the engine host on the sandbox origin, and this
	// map holds its stand-in (app/engine-bridge.js); first-party pages stay iframes here.
	const R = remote();
	const far = !!R && !tab.internal;
	if (f && !!f.rvRemote !== far) { dropFrame(tab.id); f = null; }
	if (!f && far) { f = R.frame(tab); frames.set(tab.id, f); }
	if (!f) {
		f = document.createElement("iframe");
		f.className = "rv-frame";
		f.setAttribute("sandbox", "allow-forms allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-modals allow-pointer-lock allow-downloads allow-presentation allow-orientation-lock");
		f.allow = "autoplay; fullscreen; picture-in-picture; gamepad; pointer-lock; clipboard-read; clipboard-write; screen-wake-lock; encrypted-media; accelerometer; gyroscope; magnetometer";
		f.allowFullscreen = true;
		// The recovery ladder (errors.js) handles failures here, so our worker's error pages skip their own retry.
		f.setAttribute("data-rv-ladder", "");
		f.hidden = true;
		frames.set(tab.id, f); // register before insert (sync about:blank load would recurse otherwise)
		f.addEventListener("load", () => onFrameLoad(tab.id));
		contentEl.insertBefore(f, folderEl);
	}
	const want = tab.internal || tab.src;
	if (want && f.getAttribute("src") !== want) {
		if (!isGamePath(want)) { f.rvPassWait = null; f.setAttribute("src", want); }
		else if (f.rvPassWait !== want) {
			// A library game loads once the library pass is in place (app/play-pass.js).
			f.rvPassWait = want;
			playPass().then(() => {
				if (f.rvPassWait !== want) return;
				f.rvPassWait = null;
				if ((tab.internal || tab.src) === want && f.isConnected) f.setAttribute("src", want);
			});
		}
	}
	return f;
}

export function dropFrame(id) {
	const f = frames.get(id);
	if (f) { f.remove(); frames.delete(id); }
}

// Force a real refetch of a proxied tab's current src. Reassigning the same src
// attribute won't reload (ensureFrame skips an unchanged value), so we drop the
// iframe and let render() recreate it - the reliable way to retry a failed load or
// re-fetch through a just-switched transport, without pushing a new history entry.
export function reloadFrame(id) {
	const t = byId(id);
	if (!t || t.internal || !t.src) return;
	dropFrame(id);
	render();
}

// Sandbox mode: the engine host reports a proxied tab's load (what onFrameLoad reads below, read on
// the sandbox origin). Data only: an address (http(s), already checked), a title, then the favicon.
export function remoteNav(id, d) {
	const t = byId(id), f = frames.get(id);
	if (!t || !f || !f.rvRemote || t.internal || !t.src) return;
	t.loading = false;
	clearWatch(id);
	if (d.url) {
		if (get("incognito")) t.priv = true; // a page reached in incognito is never saved for "Restore tabs"
		t.url = d.url;
		t.addr = d.url;
		if (id === state.activeId && !state.editing) { addrInput.value = dispAddr(d.url); addrInput.dataset.real = d.url; }
	}
	t.title = d.title || (t.url ? t.url.replace(/^[a-z]+:\/\//i, "").split("/")[0] : t.title);
	recordHistory(t.url, t.title);
	pageLoaded(id);
	render();
}
export function remoteIcon(id, fav) {
	const t = byId(id);
	if (!t || t.internal || !t.src) return;
	t.favicon = fav;
	render();
}

function onFrameLoad(id) {
	const t = byId(id);
	if (!t) return;
	t.loading = false;
	clearWatch(id);
	const f = frames.get(id);
	if (f && t.src && !t.internal) {
		// Reflect in-page navigation / redirects in the address bar + tab title.
		try {
			const href = f.contentWindow.location.href || "";
			let real = null;
			if (href.indexOf(PREFIXES.sj2) !== -1 || href.indexOf(PREFIXES.sj) !== -1 || href.indexOf(PREFIXES.uv) !== -1) real = decode(href);
			else if (/^https?:\/\//i.test(href) && href.indexOf(location.origin) !== 0) real = href;
			if (real) {
				if (get("incognito")) t.priv = true;
				t.url = real;
				t.addr = real;
				if (id === state.activeId && !state.editing) { addrInput.value = dispAddr(real); addrInput.dataset.real = real; }
			}
			const docTitle = f.contentDocument && f.contentDocument.title;
			t.title = docTitle || (t.url ? t.url.replace(/^[a-z]+:\/\//i, "").split("/")[0] : t.title);
		} catch (e) {}
		// Resolve a same-origin (proxied) favicon for the tab strip. Inside the
		// proxied iframe, <link rel=icon>.href already resolves to a proxied URL;
		// otherwise route a real URL / the /favicon.ico fallback through the proxy.
		try {
			const doc = f.contentDocument;
			const link = doc && doc.querySelector('link[rel~="icon" i]');
			let fav = null;
			if (link && link.href) {
				const h = link.href;
				fav = h.startsWith("data:") ? h
					: h.startsWith(location.origin) ? h // already proxied - keep verbatim
					: /^https?:/i.test(h) ? encode(h, t) : null; // re-proxy an absolute real URL
			}
			if (!fav && t.url) { try { fav = encode(new URL(t.url).origin + "/favicon.ico", t); } catch (e) {} }
			// Leak guard: encode() returns a same-origin path (absolute or relative
			// "/<prefix>/…"); it returns the input UNCHANGED if the engine isn't ready.
			// Accept data:, our origin, or a same-origin relative path - but never a
			// real "http(s)://" or protocol-relative "//host" URL (those would leak).
			t.favicon = fav && (fav.startsWith("data:") || fav.startsWith(location.origin) || (fav.charAt(0) === "/" && fav.charAt(1) !== "/")) ? fav : null;
		} catch (e) {}
		recordHistory(t.url, t.title);
		applyZoom(f, t.zoom);
		// Detect an engine error page. checkFrame runs once now, but Scramjet 1 renders
		// its own "Uh oh!" error DOM ASYNCHRONOUSLY, a beat after this load event - so
		// armProxyErrorCheck also re-checks a few times to catch that (otherwise the raw
		// engine error page is left showing instead of our themed, actionable overlay).
		pageLoaded(id);
		armProxyErrorCheck(id); // before checkFrame: a failure found now clears these again
		checkFrame(id);
		applyExtFrame(id); // inject enabled page-tool extensions (dark mode, devtools)
	} else if (f && t.internal && !t.error) {
		// A first-party page (game / tool) that 404s loads our branded 404 doc
		// inside the iframe - surface the Retry overlay instead of a raw 404.
		try { if (f.contentDocument && f.contentDocument.querySelector(".rv-404")) failInternal(id); } catch (e) {}
	}
	if (f && !f.rvRemote) { try { watchFrameKeys(f.contentWindow); } catch (e) {} }
	render();
	// A page the student just opened (a book, a tool, a link) takes focus once it loads, unless
	// they have already moved on to something else, like the address bar.
	// The empty about:blank load that comes with a new frame doesn't count.
	if (f && t.focusOnLoad && id === state.activeId && !f.hidden && f.getAttribute("src")) {
		t.focusOnLoad = false;
		const ae = document.activeElement;
		// A control that just went away (the closed palette, a hidden view) doesn't count as moving on.
		const sent = ae === addrInput && addrInput.dataset.sent === "1";
		const busy = ae && ae !== document.body && ae !== f && ae !== contentEl && !sent && !folderEl.contains(ae) && !startEl.contains(ae) && ae.getClientRects().length > 0;
		delete addrInput.dataset.sent;
		if (!busy) { try { f.focus(); } catch (e) {} }
	}
}

// Every tab is one fixed-width cell (CSS: --tab-w), so a tab's ✕ always sits at
// the same offset and closing a run of tabs is repeated clicks in ONE spot. That
// only holds while the widths stay put, so once tabs are narrow enough to be in
// the shrink range we pin the current width for as long as the pointer is over
// the strip - otherwise every close would re-widen the survivors and slide the
// next ✕ out from under the cursor. Released on pointerleave (touch pointers
// leave on lift, so this self-releases there too).
const stripEl = tabsEl && tabsEl.parentElement;
function lockTabWidth() {
	if (!tabsEl || tabsEl.style.getPropertyValue("--tab-w")) return; // already pinned
	const first = tabsEl.firstElementChild;
	if (!first || state.tabs.length < 2) return;
	const w = first.getBoundingClientRect().width;
	if (w > 0) tabsEl.style.setProperty("--tab-w", w + "px");
}
function unlockTabWidth() { if (tabsEl) tabsEl.style.removeProperty("--tab-w"); }
if (stripEl) stripEl.addEventListener("pointerleave", unlockTabWidth);
// Wheel over the strip scrolls it sideways (it scrolls instead of clipping once
// the tabs bottom out at --tab-w-min).
if (tabsEl) {
	tabsEl.addEventListener("wheel", (e) => {
		if (e.shiftKey || e.ctrlKey || !e.deltaY) return;
		if (tabsEl.scrollWidth <= tabsEl.clientWidth) return;
		tabsEl.scrollLeft += e.deltaY;
		e.preventDefault();
	}, { passive: false });
}

let scrolledActive = null; // tab id the strip was last auto-scrolled to

const focusActiveTab = () => { const el = tabsEl.querySelector('.rv-tab[tabindex="0"]'); if (el) el.focus(); };
// Tab strip keyboard: arrows and Home/End switch tabs (wrapping), Ctrl+Shift+arrows move the tab,
// Delete closes it, Shift+F10 or the ContextMenu key opens its menu.
function onTabKey(e, t, el) {
	const i = state.tabs.findIndex((x) => x.id === t.id);
	const n = state.tabs.length;
	if (e.key === "Enter" || e.key === " ") { e.preventDefault(); selectTab(t.id); return; }
	if (e.key === "Delete" && !t.pinned) { e.preventDefault(); lockTabWidth(); closeTab(t.id); focusActiveTab(); return; }
	if ((e.key === "F10" && e.shiftKey) || e.key === "ContextMenu") {
		e.preventDefault();
		const r = el.getBoundingClientRect();
		openTabMenu(t.id, r.left, r.bottom + 2, true);
		return;
	}
	const dir = e.key === "ArrowLeft" ? -1 : e.key === "ArrowRight" ? 1 : 0;
	if (dir && e.ctrlKey && e.shiftKey) {
		e.preventDefault();
		if (moveTab(t.id, dir)) focusActiveTab();
		return;
	}
	let to = -1;
	if (dir && !e.altKey && !e.ctrlKey && !e.metaKey) to = (i + dir + n) % n;
	else if (e.key === "Home") to = 0;
	else if (e.key === "End") to = n - 1;
	if (to < 0) return;
	e.preventDefault();
	selectTab(state.tabs[to].id);
	focusActiveTab();
}

const finePointer = () => { try { return matchMedia("(pointer: fine)").matches; } catch { return true; } };
function renderTabs() {
	const keepScroll = tabsEl.scrollLeft; // the rebuild below wipes scroll position
	tabsEl.textContent = "";
	tabsEl.setAttribute("role", "tablist");
	tabsEl.style.setProperty("--tab-n", String(state.tabs.length)); // strip asks for count x --tab-w (see .rv-tabs)
	let activeEl = null;
	state.tabs.forEach((t) => {
		const sel = t.id === state.activeId;
		const el = document.createElement("div");
		el.className = "rv-tab" + (sel ? " active" : "") + (t.pinned ? " pinned" : "");
		if (sel) activeEl = el;
		// Drag to reorder with a mouse or trackpad only. On an iPad a draggable tab lifts for a drag
		// at the same moment the long-press menu (Move left / Move right) should open, and wins.
		el.draggable = finePointer();
		el.setAttribute("role", "tab");
		el.setAttribute("aria-selected", sel ? "true" : "false");
		el.tabIndex = sel ? 0 : -1;
		// Favicon for proxied tabs only (never internal/folder/blank/errored/obfuscated
		// tabs). Painted only once confirmed loaded - so there's never a blank/broken
		// icon. A shown favicon takes the pin glyph's place.
		let favShown = false;
		if (t.favicon && t.src && !t.internal && !t.view && !t.error && !maxObf()) {
			const st = favStatus.get(t.favicon);
			if (st === undefined) probeFav(t.favicon, iconHost(t.url)); // resolve async; no icon this pass
			else if (st === "ok") {
				const fav = document.createElement("span");
				fav.className = "rv-tab-favicon";
				fav.style.backgroundImage = 'url("' + t.favicon.replace(/"/g, "%22") + '")';
				el.appendChild(fav);
				favShown = true;
			}
		}
		if (t.pinned && !favShown) { const pin = document.createElement("span"); pin.className = "rv-tab-pin"; pin.innerHTML = PIN_SVG; el.appendChild(pin); }
		const title = document.createElement("span");
		title.className = "rv-tab-title";
		title.setAttribute("data-no-obf", ""); // obfuscated explicitly below (not by the observer)
		title.textContent = dispTitle(t.title || "New Tab");
		el.appendChild(title);
		if (!t.pinned) {
			// role="tab" can't own a focusable control, so the x is a plain span for mouse
			// and touch. Keyboard users close the focused tab with Delete (keydown below).
			const close = document.createElement("span");
			close.className = "rv-tab-close";
			close.title = "Close tab";
			close.setAttribute("aria-hidden", "true");
			close.innerHTML = CLOSE_SVG;
			close.addEventListener("click", (e) => { e.stopPropagation(); lockTabWidth(); closeTab(t.id); });
			el.appendChild(close);
		}
		el.addEventListener("click", () => selectTab(t.id));
		if (!t.pinned) el.setAttribute("aria-keyshortcuts", "Delete");
		el.addEventListener("keydown", (e) => onTabKey(e, t, el));
		el.addEventListener("contextmenu", (e) => {
			e.preventDefault();
			// The ContextMenu key and Shift+F10 fire this with no pointer position: open under the tab.
			if (!e.clientX && !e.clientY) { const r = el.getBoundingClientRect(); openTabMenu(t.id, r.left, r.bottom + 2, true); }
			else openTabMenu(t.id, e.clientX, e.clientY);
		});
		attachLongPress(el, (x, y) => openTabMenu(t.id, x, y));
		el.addEventListener("dragstart", (e) => onDragStart(e, t.id));
		el.addEventListener("dragover", (e) => e.preventDefault());
		el.addEventListener("drop", (e) => onDrop(e, t.id));
		tabsEl.appendChild(el);
	});
	tabsEl.scrollLeft = keepScroll; // clamped by the browser if the strip shrank
	// Reveal the active tab when it CHANGES (a new/selected tab can be off-screen
	// once the strip scrolls) - never on an incidental re-render, which would
	// yank the strip back while the user is scrolling it.
	if (activeEl && scrolledActive !== state.activeId) {
		scrolledActive = state.activeId;
		// Scroll the strip by hand: scrollIntoView also moves Chrome's Tab starting point, which made
		// the first Tab on a fresh page skip the "Skip to content" link.
		const s = tabsEl.getBoundingClientRect(), r = activeEl.getBoundingClientRect();
		if (r.left < s.left) tabsEl.scrollLeft -= s.left - r.left;
		else if (r.right > s.right) tabsEl.scrollLeft += r.right - s.right;
	}
	// Drop probe entries for favicons no longer on any open tab so favStatus can't
	// grow unbounded across a long session of navigations.
	if (favStatus.size > state.tabs.length) {
		const live = new Set(state.tabs.map((t) => t.favicon).filter(Boolean));
		for (const k of favStatus.keys()) if (!live.has(k)) favStatus.delete(k);
	}
}

// The connection follows the ACTIVE tab's site: an automatic switch made for one site (errors.js)
// must not carry over to other tabs, the start page, Back/Forward or a link that left the site,
// and switching back to that site's tab restores it. transportFor() is a no-op when nothing changes.
let connUrl = null;
function syncTransport(a) {
	const url = a && !a.internal && !a.view && a.url ? a.url : "";
	if (url !== connUrl) { connUrl = url; transportFor(url).catch(() => {}); }
}

export function render() {
	if (document.documentElement.dataset.layout !== "island") renderTabs();
	const a = active();
	syncTransport(a);

	backBtn.disabled = !(a && a.hi > 0);
	fwdBtn.disabled = !(a && a.hi < a.hist.length - 1);
	spinner.hidden = !(a && a.loading);
	updateStar();

	// Keep only the most-recently-used tabs "live" (with an iframe). Beyond the
	// cap, inactive tabs are suspended - their iframe is dropped so a stack of
	// open game/proxy tabs can't lag the BiB; reopening one reloads it.
	const live = liveSetIds();
	state.tabs.forEach((tab) => {
		if (live.has(tab.id)) {
			if (tab.suspended) { tab.suspended = false; if (tab.src || tab.internal) { tab.loading = true; tab.skipLoader = false; } }
			ensureFrame(tab);
		} else {
			if (frames.has(tab.id)) dropFrame(tab.id);
			tab.suspended = true;
		}
	});
	frames.forEach((f, id) => {
		const t = byId(id);
		const showFrame = !!(t && a && t.id === a.id && (t.src || t.internal) && !t.view && !t.error);
		if (t) { const ft = dispTitle(t.title || "Page"); if (f.title !== ft) f.title = ft; }
		const changed = f.hidden !== !showFrame;
		f.hidden = !showFrame;
		// Internal pages can't see their own display:none, so tell them (the Linux page pauses its VM).
		if (changed && t && t.internal) { try { f.contentWindow.postMessage({ type: "revault-frame-visible", visible: showFrame }, location.origin); } catch (e) {} }
	});

	const showError = !!(a && a.error);
	const showFolder = !!(a && a.view && !a.error);
	const showStart = !!(a && !a.src && !a.internal && !a.view && !a.loading && !a.error && !a.blank);

	errorEl.hidden = !showError;
	folderEl.hidden = !showFolder;
	startEl.hidden = !showStart;

	// Loading overlay (covers the white iframe flash; fades in only if a load is
	// slow, so fast pages never flicker it). Skippable per navigation.
	if (loaderEl) {
		// Off in Settings (Loading screen): pages show as they load; the address bar spinner stays.
		const loading = !!(a && a.loading && !a.skipLoader && get("showLoader") !== false);
		loaderEl.dataset.on = loading ? "1" : "0";
		if (loading && loaderLabel) { const h = a.url ? loadHost(a.url) : ""; loaderLabel.textContent = h ? "Loading " + h + "…" : "Loading…"; }
		renderWait(loading ? a : null);
	}

	if (showError) renderError(a);
	// Rebuild the folder grid only when the active folder view changes (the
	// Books library is 1300+ cards - don't rebuild it on every render).
	if (showFolder) {
		if (a.view !== renderedFolderView) { renderedFolderView = a.view; renderFolderView(a.view); }
	} else {
		renderedFolderView = null;
	}

	saveSession(); // debounced - persist the open tabs for next launch
	if (afterRender) afterRender();
}

let renderedFolderView = null;
